import { useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Menu, MessageCircle, Star, X } from 'lucide-react';
import { Avatar, Skeleton } from '@tcrd/ui';
import { homeFor, useAuth } from '../../stores/auth';
import { usePublicProfile, whatsappLink } from './usePublicProfile';
import { linkStoredDriver, storeDriverCode, storedDriverCode } from '../../lib/driver-invite';
import { env } from '../../lib/env';

export const ICON_MARK = '/brand/icon-mark.png';
export const LOGO_WHITE = '/brand/logo-white.png';
export const LOGO_DARK = '/brand/logo-dark.png';

const FOOTER = {
  company: [
    { to: '/info/nosotros', label: 'Quiénes somos' },
    { to: '/info/servicios', label: 'Lo que ofrecemos' },
    { to: '/info/socio', label: 'Socio conductor' },
  ],
  legal: [
    { to: '/legal/terminos', label: 'Términos del servicio' },
    { to: '/legal/privacidad', label: 'Privacidad' },
  ],
};

/** Ícono de la marca sobre un cuadro blanco (la punta negra del pin no se vería sobre fondo negro). */
export function MarkBadge({ size = 36 }: { size?: number }) {
  return (
    <span className="inline-flex items-center justify-center rounded-xl bg-white ring-1 ring-black/10 dark:ring-0 shrink-0" style={{ width: size, height: size }}>
      <img src={ICON_MARK} alt="" className="w-[78%] h-[78%] object-contain" />
    </span>
  );
}

/** Portada minimalista (estilo Uber): logo, Usuario / Socio conductor y pie con ayuda y empresa. */
export function LandingPage() {
  // /c/<código>: enlace personal de un conductor. Se recuerda para el registro.
  const { code } = useParams<{ code?: string }>();
  useEffect(() => {
    if (code) storeDriverCode(code);
  }, [code]);
  const { data, isLoading } = usePublicProfile(code ?? storedDriverCode());
  const { status, user } = useAuth();
  // Cliente con sesión que abre el enlace de un conductor: queda con ese conductor.
  useEffect(() => {
    if (code && status === 'authenticated') void linkStoredDriver(user?.roles);
  }, [code, status, user]);
  const [menu, setMenu] = useState(false);

  const authed = status === 'authenticated';
  const isCustomer = !!user?.roles.includes('CUSTOMER');
  const userHref = authed ? (isCustomer ? '/app/home' : homeFor(user)) : '/register?next=/app/request';
  const invited = data?.invitedBy ? data.drivers[0] : undefined;
  const vehicle = invited?.vehicles[0];
  const wa = whatsappLink(data?.tenant.whatsapp ?? env.whatsappNumber);
  const name = data?.tenant.name ?? 'TuConductorRD';

  useEffect(() => {
    document.title = invited ? `${invited.firstName} ${invited.lastName} — ${name}` : `${name} — Tu conductor personal`;
  }, [invited, name]);

  const Row = ({ to, href, title, subtitle }: { to?: string; href?: string; title: string; subtitle?: string }) => {
    const inner = (
      <>
        <span>
          <span className="block text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight">{title}</span>
          {subtitle && <span className="block text-sm md:text-base text-black/65 dark:text-white/60 mt-0.5">{subtitle}</span>}
        </span>
        <ArrowRight className="size-7 lg:size-9 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden />
      </>
    );
    const cls = 'group tap flex items-center justify-between gap-4 py-6 lg:py-8 border-b border-black/15 dark:border-white/25';
    return href ? (
      <a href={href} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
    ) : (
      <Link to={to!} className={cls}>{inner}</Link>
    );
  };

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-full bg-white text-black dark:bg-black dark:text-white flex flex-col">
          {/* Encabezado */}
          <header className="sticky top-0 z-20 bg-white/95 dark:bg-black/95 backdrop-blur pt-[env(safe-area-inset-top)]">
            <div className="mx-auto max-w-6xl px-4 sm:px-5 md:px-8 h-16 md:h-20 flex items-center justify-between gap-2 sm:gap-3">
              <Link to="/" className="flex items-center gap-2.5 min-w-0" aria-label={name}>
                <MarkBadge />
                {/* En teléfono solo el ícono: el nombre no cabe junto a los botones. */}
                <span className="font-semibold truncate hidden sm:inline">{name}</span>
              </Link>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                {authed ? (
                  <Link to={homeFor(user)} className="tap rounded-full bg-black text-white dark:bg-white dark:text-black px-4 py-2 text-sm font-semibold">Mi cuenta</Link>
                ) : (
                  <>
                    <Link to="/login" className="tap rounded-full bg-black/5 dark:bg-white/10 px-3 sm:px-3.5 py-2 text-[13px] sm:text-sm font-semibold whitespace-nowrap">Inicia sesión</Link>
                    <Link to="/register" className="tap rounded-full bg-black text-white dark:bg-white dark:text-black px-3 sm:px-3.5 py-2 text-[13px] sm:text-sm font-semibold whitespace-nowrap">Regístrate</Link>
                  </>
                )}
                <button className="tap size-9 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center" onClick={() => setMenu(true)} aria-label="Menú">
                  <Menu className="size-5" />
                </button>
              </div>
            </div>
          </header>

          {/* Logo + opciones: una columna en teléfono y tableta; dos columnas en computadora. */}
          <div className="mx-auto w-full max-w-6xl lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center lg:min-h-[calc(100dvh-5rem)] lg:px-8">
          <section className="border-b border-black/10 dark:border-white/15 lg:border-b-0">
            <div className="px-5 py-14 md:py-20 lg:py-0 flex justify-center lg:justify-start">
              {/* Logo oficial sin fondo: oscuro en el tema claro, blanco en el tema oscuro. */}
              <img src={LOGO_DARK} alt={name} className="w-full max-w-[min(22rem,80vw)] md:max-w-[30rem] lg:max-w-[34rem] h-auto object-contain dark:hidden" />
              <img src={LOGO_WHITE} alt={name} className="w-full max-w-[min(22rem,80vw)] md:max-w-[30rem] lg:max-w-[34rem] h-auto object-contain hidden dark:block" />
            </div>
          </section>

          {/* Opciones */}
          <section className="mx-auto w-full max-w-3xl lg:max-w-none px-5 md:px-8 lg:px-0 pb-10 lg:pb-0">
            {isLoading && <Skeleton className="h-40 mt-6 bg-black/5 dark:bg-white/10" />}
            {!isLoading && invited ? (
              // Enlace personal de un conductor
              <>
                <div className="flex items-center gap-4 py-7 border-b border-black/15 dark:border-white/25">
                  <Avatar src={invited.photoUrl} firstName={invited.firstName} lastName={invited.lastName} size={64} />
                  <div className="min-w-0">
                    <p className="text-sm text-black/65 dark:text-white/60">Tu conductor</p>
                    <p className="text-2xl font-bold truncate">{invited.firstName} {invited.lastName}</p>
                    <p className="text-sm text-black/75 dark:text-white/70 truncate">
                      {vehicle ? `${vehicle.make} ${vehicle.model} · ${vehicle.color}` : 'Conductor privado'}
                      {invited.rating.count > 0 && (
                        <span className="inline-flex items-center gap-1 ml-2">
                          <Star className="size-3.5 fill-current text-amber-400" aria-hidden /> {invited.rating.average}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <Row to={authed && isCustomer ? '/app/request' : '/register?next=/app/request'} title={`Pedir viaje con ${invited.firstName}`} subtitle="Cotiza, acepta y síguelo en vivo" />
                {!authed && <Row to="/login" title="Ya tengo cuenta" />}
              </>
            ) : (
              !isLoading && (
                <>
                  <Row to={userHref} title="Usuario" subtitle="Pide tu viaje y síguelo en vivo" />
                  <Row to="/info/socio" title="Socio conductor" subtitle="Maneja con nosotros" />
                </>
              )
            )}
          </section>
          </div>

          {/* Pie */}
          <footer className="mt-auto border-t border-black/10 dark:border-white/15 pb-[env(safe-area-inset-bottom)]">
            <div className="mx-auto max-w-6xl px-5 md:px-8 py-12 md:py-16 flex flex-col md:flex-row md:flex-wrap md:justify-between gap-10">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                  <MarkBadge size={32} />
                  <span className="font-semibold">{name}</span>
                </div>
                <Link to="/info/ayuda" className="tap text-black/90 dark:text-white/90 hover:underline w-fit">Visita el Centro de ayuda</Link>
                {wa && (
                  <a href={wa} target="_blank" rel="noreferrer" className="tap inline-flex items-center gap-2 text-black/90 dark:text-white/90 hover:underline w-fit">
                    <MessageCircle className="size-4" aria-hidden /> Escríbenos por WhatsApp
                  </a>
                )}
              </div>
              <div className="grid grid-cols-2 gap-8 md:gap-20">
                <nav aria-label="Compañía">
                  <p className="font-bold mb-3">Compañía</p>
                  <ul className="flex flex-col gap-2.5 text-sm text-black/85 dark:text-white/80">
                    {FOOTER.company.map((l) => (
                      <li key={l.to}><Link to={l.to} className="hover:underline">{l.label}</Link></li>
                    ))}
                  </ul>
                </nav>
                <nav aria-label="Legal">
                  <p className="font-bold mb-3">Legal</p>
                  <ul className="flex flex-col gap-2.5 text-sm text-black/85 dark:text-white/80">
                    {FOOTER.legal.map((l) => (
                      <li key={l.to}><Link to={l.to} className="hover:underline">{l.label}</Link></li>
                    ))}
                  </ul>
                </nav>
              </div>
              <p className="md:w-full text-xs text-black/55 dark:text-white/50">© {new Date().getFullYear()} {name} · República Dominicana</p>
            </div>
          </footer>
        </div>

        {/* Menú */}
        {menu && (
          <div className="fixed inset-0 z-50 bg-white text-black dark:bg-black dark:text-white flex flex-col pt-[env(safe-area-inset-top)]" role="dialog" aria-modal="true" aria-label="Menú">
            <div className="mx-auto w-full max-w-3xl px-5 h-16 flex items-center justify-between">
              <span className="flex items-center gap-2.5"><MarkBadge /> <span className="font-semibold">{name}</span></span>
              <button className="tap size-9 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center" onClick={() => setMenu(false)} aria-label="Cerrar menú">
                <X className="size-5" />
              </button>
            </div>
            <nav className="mx-auto w-full max-w-3xl px-5 flex flex-col" onClick={() => setMenu(false)}>
              {[{ to: userHref, label: 'Usuario' }, { to: '/info/socio', label: 'Socio conductor' }, { to: '/info/ayuda', label: 'Centro de ayuda' }, ...FOOTER.company.filter((l) => l.to !== '/info/socio')].map((l) => (
                <Link key={l.label} to={l.to} className="tap py-5 text-2xl font-bold border-b border-black/10 dark:border-white/15 flex items-center justify-between">
                  {l.label} <ArrowRight className="size-6" aria-hidden />
                </Link>
              ))}
              {!authed && (
                <div className="grid grid-cols-2 gap-3 pt-6">
                  <Link to="/login" className="tap rounded-full bg-black/5 dark:bg-white/10 py-3 text-center font-semibold">Inicia sesión</Link>
                  <Link to="/register" className="tap rounded-full bg-black text-white dark:bg-white dark:text-black py-3 text-center font-semibold">Regístrate</Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}
